import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Crop } from '@next/arable-lib/crop';
import { CropDateService } from '@next/arable-lib/crop-field';
import { GeoCalculationService, GeoCalculationServiceInjector } from '@next/arable-lib/geo';
import { DialogService } from '@next/core-lib/dialog';
import { AgrDateValidators } from '@next/core-lib/form';
import { LocaleService } from '@next/core-lib/i18n';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, skip, startWith, withLatestFrom } from 'rxjs/operators';

import {
  isGroundWaterPreservationWithNitrogenCuts,
  isNutrientsPollutedArea,
} from 'src/app/shared/helpers/nutrient-polluted.helper';
import { SoilAnalysisSortHelper } from 'src/app/soil-analysis/helpers/SoilAnalysisSortHelper';
import * as SoilAnalysisActions from 'src/app/soil-analysis/store/soil-analysis.actions';

import { selectAvailableTgaCrops } from 'src/app/store/selectors';

import { CropField, CropFieldType, CropVariety, Province, SiteState } from '../../../shared/models';
import { DataExchangeService } from '../../../shared/services/data-exchange.service';
import {
  ManualSoilAnalysis,
  SoilAnalysis,
  StateEnum,
} from '../../../soil-analysis/models/soil-analysis.model';
import {
  selectDefaultSoilAnalyses,
  selectSoilAnalyses,
} from '../../../soil-analysis/store/soil-analysis.selectors';
import { LoadCropVarietiesByCropIdRequest } from '../../store/actions/crop-variety.actions';
import { isPrefilling } from '../../store/selectors/crop-field.selectors';
import { getCropVarietiesByCropId } from '../../store/selectors/crop-variety.selectors';
import { getSoilTypes } from '../../store/selectors/soil-type.selectors';
import { QuickEditDialogModel } from '../quick-edit-dialog/quick-edit-dialog.model';

@Component({
  selector: 'app-cropfield-detail',
  templateUrl: './cropfield-detail.component.html',
  styleUrls: ['./cropfield-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CropfieldDetailComponent implements OnInit, OnChanges, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actionsListener$ = inject<ActionsSubject>(ActionsSubject);
  private readonly geoCalculation = inject<GeoCalculationService>(GeoCalculationServiceInjector);
  private readonly locale = inject(LocaleService);
  private readonly cropDateService = inject(CropDateService);
  private readonly dialog = inject(DialogService);
  private readonly dataExchangeService = inject(DataExchangeService);

  @Input() formGroup: FormGroup;
  @Input() data: QuickEditDialogModel;
  @Input() cropField: CropField;
  @Input() bemCropVarieties?: CropVariety[];

  private readonly subscription = new Subscription();
  private readonly countToSkipInitialLoad = 1;

  isPrefilling$ = this.store.pipe(select(isPrefilling));

  eProvince = Province;

  filteredCrops$: Observable<Crop[]>;

  filteredCropVarieties$: Observable<CropVariety[]>;
  concepts$ = this.dataExchangeService.getConcepts();
  buyerChainPartners$ = this.dataExchangeService.getBuyerChainPartners();
  cropVarietiesByCropIds$ = this.store.pipe(select(getCropVarietiesByCropId));
  cropVarieties: CropVariety[] = [];
  currentCropVariety?: CropVariety;

  soilAnalysesControl = new FormControl();
  @ViewChild('soilAnalysesInput') soilAnalysesInput: ElementRef<HTMLInputElement>;
  soilAnalysesFormArray: FormArray;
  availableSoilAnalyses$: Observable<SoilAnalysis[]>;
  availableSoilAnalyses: SoilAnalysis[] = [];
  availableDefaultSoilAnalyses$: Observable<SoilAnalysis[]>;
  availableDefaultSoilAnalyses: SoilAnalysis[] = [];
  selectedSoilAnalysisIds$: Observable<number[]>;
  selectedSoilAnalyses$: Observable<SoilAnalysis[]>;
  soilAnalyses$ = this.store.pipe(select(selectSoilAnalyses));
  defaultSoilAnalyses$ = this.store.pipe(select(selectDefaultSoilAnalyses));
  filteredSoilAnalyses$: Observable<SoilAnalysis[]>;
  filteredDefaultSoilAnalyses$: Observable<SoilAnalysis[]>;
  availableTgaCrops$ = this.store.pipe(select(selectAvailableTgaCrops));
  soilTypes$ = this.store.pipe(select(getSoilTypes));
  nutrientsPollutedValues$: Observable<{
    province: Province;
    isGroundWaterPreservation: boolean;
    soilTypeId: number;
  }>;
  availableTgaCrops: number[] = [];

  separatorKeysCodes: number[] = [ENTER, COMMA];
  justAddedNewSoilAnaysis = false;

  currentCropId: number;
  shownTgaWarning: boolean;

  ngOnInit(): void {
    this.currentCropId = this.formGroup.controls.cropId.value;
    this.shownTgaWarning = false;

    this.filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) =>
        this.removeDuplicateCrops([
          ...this.filter(value, [...this.data.mostUsedCrops]),
          ...this.filter(value, [...this.data.crops]),
        ]),
      ),
    );

    if (this.isParticipatingToBem()) {
      this.formGroup.get('cropId').disable();
      this.formGroup.get('cropVarietyId')?.disable();

      if (this.canChangeBemCropVariety()) {
        this.applyBemCropVarieties();
      }
    }

    this.filteredSoilAnalyses$ = this.soilAnalysesControl.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterSoilAnalyses(value, this.availableSoilAnalyses)),
    );

    this.filteredDefaultSoilAnalyses$ = this.soilAnalysesControl.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterSoilAnalyses(value, this.availableDefaultSoilAnalyses)),
    );

    this.setCropVarietiesSubscription();

    this.setProvinceField(this.cropField.outsideDutchBorders);

    this.setupUpdateNutrientsPollutedValues();

    this.subscription.add(
      this.formGroup.get('outsideDutchBorders').valueChanges.subscribe((outsideDutchBorders) => {
        this.formGroup
          .get('province')
          .setValidators(this.getProvinceValidators(outsideDutchBorders));

        this.setProvinceField(outsideDutchBorders);
      }),
    );

    this.subscription.add(
      this.formGroup.get('isAreaCalculated').valueChanges.subscribe((isAreaCalculated) => {
        if (isAreaCalculated) {
          this.setAreaInHectares();
        }
      }),
    );
    this.subscription.add(
      this.formGroup.get('areaInHectares').valueChanges.subscribe((_) => {
        this.formGroup.get('isAreaCalculated').setValue(false);
      }),
    );
    this.subscription.add(
      this.formGroup.get('cropId').valueChanges.subscribe((cropId: number) => {
        if (cropId) {
          const crop = this.data.crops.find((c) => c.id === cropId);
          if (crop) {
            if (this.cropField.isSubsequentCultivation) {
              this.setCalculatedEndDate(crop);
            } else {
              this.setDefaultStartAndEndDate(crop);
            }

            this.loadCropVarieties(cropId);
            this.formGroup.get('cropVarietyId').setValue(null);

            if (
              this.cropField?.cropFieldType === CropFieldType.Advice &&
              this.availableTgaCrops?.findIndex((x) => x === cropId) === -1 &&
              this.availableTgaCrops?.findIndex((x) => x === this.currentCropId) > -1 &&
              !this.shownTgaWarning
            ) {
              this.shownTgaWarning = true;
              this.dialog.notify({
                title: this.locale.translate('tgaCropChangeWarningTitle'),
                text: this.locale.translate('tgaCropChangeWarningText'),
                cancelButtonText: this.locale.translate('close'),
                pushHistoryState: false,
              });
            } else if (
              this.availableTgaCrops?.findIndex((x) => x === cropId) > -1 &&
              this.shownTgaWarning
            ) {
              this.shownTgaWarning = false;
            }
          } else {
            this.formGroup.get('cropId').setValue(null, { emitModelToViewChange: false });
          }
        }
      }),
    );

    this.subscription.add(
      this.formGroup
        .get('cropVarietyId')
        .valueChanges.subscribe((cropVarietyId: number | string) => {
          if (typeof cropVarietyId === 'string') {
            this.formGroup.get('cropVarietyId').setValue(null, { emitModelToViewChange: false });
          }
        }),
    );

    this.subscription.add(
      this.formGroup.get('startDate').valueChanges.subscribe((value: Date) => {
        this.formGroup.get('endDate').setValidators(this.getEndDateValidators(value));
        this.formGroup.get('endDate').updateValueAndValidity({ emitEvent: false });
      }),
    );
    this.subscription.add(
      this.formGroup.get('endDate').valueChanges.subscribe((value: Date) => {
        this.formGroup.get('startDate').setValidators(this.getStartDateValidators(value));
        this.formGroup.get('startDate').updateValueAndValidity({ emitEvent: false });
      }),
    );

    this.subscription.add(
      this.actionsListener$
        .pipe(ofType(SoilAnalysisActions.soilAnalysisUpserted))
        .subscribe((upserted) => {
          if (this.justAddedNewSoilAnaysis) {
            this.soilAnalysesFormArray.push(new FormControl(upserted.id));
            this.justAddedNewSoilAnaysis = false;
          }
        }),
    );

    this.soilAnalysesFormArray = this.getSoilAnalysesFormArray();
    this.selectedSoilAnalysisIds$ = this.soilAnalysesFormArray.valueChanges.pipe(
      startWith(this.cropField.soilAnalysisIds),
    );

    this.availableSoilAnalyses$ = combineLatest([
      this.soilAnalyses$,
      this.selectedSoilAnalysisIds$,
    ]).pipe(
      map(([analyses, ids]: [SoilAnalysis[], number[]]) =>
        SoilAnalysisSortHelper.sortDesc(analyses.filter(({ id = -1 }) => ids.indexOf(id) === -1)),
      ),
    );
    this.availableDefaultSoilAnalyses$ = combineLatest([
      this.defaultSoilAnalyses$,
      this.selectedSoilAnalysisIds$,
    ]).pipe(
      map(([soilAnalyses, soilAnalysisIds]: [SoilAnalysis[], number[]]) =>
        soilAnalyses.filter(
          ({ id = -1, state }) => state === StateEnum.Active && soilAnalysisIds.indexOf(id) === -1,
        ),
      ),
    );

    this.selectedSoilAnalyses$ = combineLatest([
      this.soilAnalyses$,
      this.defaultSoilAnalyses$,
      this.selectedSoilAnalysisIds$,
    ]).pipe(
      map(
        ([soilAnalyses, defaultSoilAnalyses, soilAnalysisIds]: [
          SoilAnalysis[],
          SoilAnalysis[],
          number[],
        ]) =>
          soilAnalysisIds.map((soilAnalysisId) =>
            soilAnalyses.concat(defaultSoilAnalyses).find(({ id }) => id === soilAnalysisId),
          ),
      ),
    );

    this.availableSoilAnalyses$.subscribe(
      (availableSoilAnalyses) => (this.availableSoilAnalyses = availableSoilAnalyses),
    );

    this.availableDefaultSoilAnalyses$.subscribe(
      (availableDefaultSoilAnalyses) =>
        (this.availableDefaultSoilAnalyses = availableDefaultSoilAnalyses),
    );

    this.availableTgaCrops$.subscribe(
      (availableTgaCrops) => (this.availableTgaCrops = availableTgaCrops),
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['bemCropVarieties'] &&
      this.formGroup &&
      this.cropField &&
      this.canChangeBemCropVariety()
    ) {
      this.applyBemCropVarieties();
    }
  }

  getSoilAnalysisYear(soilAnalysis: SoilAnalysis) {
    const soilAnalysisYear = Number(soilAnalysis?.samplingDateTime?.substring(0, 4));
    return soilAnalysis && soilAnalysisYear ? `(${soilAnalysisYear})` : '';
  }

  setCropVarietiesSubscription(): void {
    this.subscription.add(
      this.cropVarietiesByCropIds$.subscribe((cropVarietiesByCropIds) => {
        const selectedCropId = this.formGroup.get('cropId').value;
        const selectedCropVarietyId =
          this.formGroup.get('cropVarietyId').value || this.cropField.cropVarietyId;
        const allCropVarieties =
          cropVarietiesByCropIds.find((x) => x.id === selectedCropId)?.cropVarieties ?? [];
        this.currentCropVariety = allCropVarieties.find(
          (variety) => variety.id === this.cropField.cropVarietyId,
        );

        if (this.canChangeBemCropVariety()) {
          this.applyBemCropVarieties();
          return;
        }

        this.cropVarieties = allCropVarieties;
        const varietyData = this.cropVarieties.filter(
          (variety) => variety.id === selectedCropVarietyId,
        );

        if (varietyData.length) {
          this.formGroup.get('cropVarietyId').setValue(selectedCropVarietyId);
        } else {
          this.formGroup.get('cropVarietyId').setValue(null);
        }

        this.setFilteredCropVarieties();
      }),
    );
  }

  displayCrop() {
    return (cropId?: number) => (cropId ? this.data.crops.find((c) => c.id === cropId).name : '');
  }

  displayCropVariety() {
    return (id?: number) =>
      this.cropVarieties.find((cropVariety) => cropVariety.id === id)?.name ??
      (this.currentCropVariety?.id === id ? this.currentCropVariety.name : '');
  }

  setDefaultStartAndEndDate(crop: Crop) {
    const startYear = crop.isStartDateInPreviousYear ? this.data.cropYear - 1 : this.data.cropYear;
    const startMonth = crop.defaultStartDate.month;
    const startDay = crop.defaultStartDate.day;

    this.setStartDate(startYear, startMonth, startDay);

    const endYear = this.data.cropYear;
    const endMonth = crop.defaultEndDate.month;
    const endDay = crop.defaultEndDate.day;

    this.setEndDate(endYear, endMonth, endDay);
  }

  setCalculatedEndDate(crop: Crop) {
    this.cropDateService
      .calculateCropDates(crop.id, new Date(this.formGroup.get('startDate').value as string))
      .subscribe((data) => {
        this.formGroup.get('endDate').setValue(new Date(data));
      });
  }

  getSoilAnalysesFormArray() {
    return this.formGroup.get('soilAnalysisIds') as FormArray;
  }

  addSoilAnalysis(event: MatChipInputEvent): void {
    const input = event.input;

    if (input) {
      input.value = '';
    }

    this.soilAnalysesControl.setValue(null);

    this.justAddedNewSoilAnaysis = true;
  }

  removeSoilAnalysis(index: number): void {
    this.soilAnalysesFormArray.removeAt(index);
    this.formGroup.markAsDirty();
  }

  selectSoilAnalysis(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value === false) {
      this.store.dispatch(
        SoilAnalysisActions.editManualSoilAnalysis({
          manualSoilAnalysis: {
            id: 0,
            characteristics: {},
            samplingDateTime: new Date().toISOString(),
          } as ManualSoilAnalysis,
        }),
      );
    } else {
      const selectedSoilAnalysisId = event.option.value as string;
      this.soilAnalysesFormArray.push(new FormControl(parseInt(selectedSoilAnalysisId, 10)));
    }
    this.soilAnalysesInput.nativeElement.value = '';
    this.soilAnalysesControl.setValue(null);
    this.formGroup.markAsDirty();
  }

  private filter(value: string, items: Crop[]): Crop[];
  private filter(value: string, items: CropVariety[]): CropVariety[];
  private filter(value: string, items: Crop[] | CropVariety[]) {
    return [...items]
      .sort((a, b) =>
        !(a.name && b.name) ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )
      .filter((option) =>
        !option.name ? false : option.name.toLowerCase().includes(value.toLowerCase()),
      );
  }

  private filterSoilAnalyses(value: string, items: SoilAnalysis[]) {
    return [...items]
      .sort((a, b) =>
        !(a.description && b.description)
          ? 0
          : a.description.toLowerCase() > b.description.toLowerCase()
            ? 1
            : -1,
      )
      .filter((option) =>
        !option.description
          ? false
          : option.description.toLowerCase().includes(value.toLowerCase()),
      );
  }

  private getProvinceValidators(outsideDutchBorders: boolean) {
    return outsideDutchBorders ? [] : [Validators.required];
  }

  private setProvinceField(outsideDutchBorders: boolean): void {
    const provinceControl = this.formGroup.get('province');
    if (outsideDutchBorders) {
      provinceControl.setValue(null);
      provinceControl.disable();
    } else {
      provinceControl.enable();
    }

    provinceControl.markAsTouched();
  }

  private removeDuplicateCrops(items: Crop[]): Crop[] {
    return items.filter(
      (v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i,
    );
  }

  private setAreaInHectares() {
    const feature = this.cropField.feature;
    this.formGroup.get('areaInHectares').setValue(this.geoCalculation.getAreaInHectares(feature), {
      emitEvent: false,
    });
  }

  private getStartDateValidators(endDate: Date) {
    const validators = [Validators.required];
    if (endDate) {
      const minYear = endDate.getFullYear() - 100;
      validators.push(AgrDateValidators.minDate(new Date(minYear, 1, 1)));
      validators.push(AgrDateValidators.maxDate(endDate));
    }
    return validators;
  }

  private getEndDateValidators(startDate: Date) {
    const validators = [Validators.required];
    if (startDate) {
      validators.push(AgrDateValidators.minDate(startDate));
    }

    const maxYear = (startDate ? startDate.getFullYear() : new Date().getFullYear()) + 100;
    validators.push(AgrDateValidators.maxDate(new Date(maxYear, 12, 31)));
    return validators;
  }

  private setStartDate(year: number, month: number, day: number) {
    this.formGroup.get('startDate').setValue(new Date(year, month - 1, day));
  }

  private setEndDate(year: number, month: number, day: number) {
    this.formGroup.get('endDate').setValue(new Date(year, month - 1, day));
  }

  /**
   * Called when the CropVariety selected by the user has changed. Will fetch
   * new the new CropVarieties for the Crop, only if they don't already exist
   * in the store.
   */
  private loadCropVarieties(cropId: number) {
    if (cropId) {
      // This is a fix to prevent the ExpressionChangedAfterItHasBeenCheckedError.
      // For an unkown reason a store dispatch call triggers this error.
      // A technical debt PBI #60929 is created to refactor this component.
      setTimeout(() => {
        this.store.dispatch(new LoadCropVarietiesByCropIdRequest(cropId));
      });
    }
  }

  private setupUpdateNutrientsPollutedValues() {
    this.nutrientsPollutedValues$ = combineLatest([
      this.formGroup
        .get('province')
        .valueChanges.pipe(startWith(this.formGroup.get('province').value)),
      this.formGroup
        .get('isGroundWaterPreservation')
        .valueChanges.pipe(startWith(this.formGroup.get('isGroundWaterPreservation').value)),
      this.formGroup
        .get('soilTypeId')
        .valueChanges.pipe(startWith(this.formGroup.get('soilTypeId').value)),
    ]).pipe(
      skip(this.countToSkipInitialLoad),
      map(([province, isGroundWaterPreservation, soilTypeId]: [Province, boolean, number]) => ({
        province,
        isGroundWaterPreservation,
        soilTypeId,
      })),
    );

    this.subscription.add(
      this.nutrientsPollutedValues$
        .pipe(withLatestFrom(this.soilTypes$))
        .subscribe(([{ province, isGroundWaterPreservation, soilTypeId }, soilTypes]) => {
          this.formGroup
            .get('isNutrientsPollutedArea')
            .setValue(isNutrientsPollutedArea(soilTypeId, soilTypes, province));
          this.formGroup
            .get('isGroundWaterPreservationWithNitrogenCuts')
            .setValue(
              isGroundWaterPreservationWithNitrogenCuts(
                soilTypeId,
                soilTypes,
                isGroundWaterPreservation,
                province,
              ),
            );
        }),
    );
  }
  isParticipatingToBem(): boolean {
    return this.cropField.isParticipatingToBem === true;
  }

  canChangeBemCropVariety(): boolean {
    return (
      this.isParticipatingToBem() &&
      this.cropField.isCancellingBem !== true &&
      this.cropField.isPlantDateConfirmed !== true
    );
  }

  isLoadingBemCropVarieties(): boolean {
    return this.canChangeBemCropVariety() && this.bemCropVarieties === undefined;
  }

  private applyBemCropVarieties(): void {
    const cropVarietyControl = this.formGroup.get('cropVarietyId');
    this.cropVarieties = this.bemCropVarieties ?? [];
    this.setFilteredCropVarieties();
    this.refreshCurrentCropVarietyDisplay(cropVarietyControl);

    if (this.bemCropVarieties?.length) {
      cropVarietyControl.enable();
    } else {
      cropVarietyControl.disable();
    }
  }

  private refreshCurrentCropVarietyDisplay(cropVarietyControl: AbstractControl): void {
    if (this.cropField.cropVarietyId) {
      cropVarietyControl.setValue(this.cropField.cropVarietyId, { emitEvent: false });
    }
  }

  private setFilteredCropVarieties(): void {
    this.filteredCropVarieties$ = this.formGroup.controls.cropVarietyId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) =>
        this.filter(value, this.cropVarieties).filter(
          (variety) =>
            variety.id === this.formGroup.get('cropVarietyId').value || !variety.isDeprecated,
        ),
      ),
    );
  }
}
